import {
  COUNTRY_LIST,
  KSA_AR_SUBDOMAIN,
  KSA_EN_SUBDOMAIN,
  REGION_LANG_TYPES,
  UAE_AR_SUBDOMAIN,
  UAE_EN_SUBDOMAIN,
} from '@common/Utility/constants';
import { setDataToCookie } from 'aster-foundation/core/common/utils/cookieHelper';
import { HOST_COUNTRY, REDIRECT_URI } from 'aster-foundation/core/common/utils/ApiConst';
import { useRouter } from 'next/router';
import { createContext, useContext, useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { IntlProvider } from 'use-intl';

const RegionLanguageContext = createContext();

export function RegionLanguageProvider({ children }) {
  const router = useRouter();
  const initialized = useRef(false);
  const langLoadedRef = useRef(null);
  const [regionLang, setRegionLang] = useState(null);
  const [selectedLanguageFile, setSelectedLanguageFile] = useState(null);

  const langCode = useMemo(() => regionLang?.split('-')?.[1], [regionLang]);

  const getRegionLangByUrl = useCallback(
    (path) => {
      const REGION_LANG_MAP = {
        [UAE_EN_SUBDOMAIN]: REGION_LANG_TYPES.UAE_EN,
        [KSA_EN_SUBDOMAIN]: REGION_LANG_TYPES.KSA_EN,
        [UAE_AR_SUBDOMAIN]: REGION_LANG_TYPES.UAE_AR,
        [KSA_AR_SUBDOMAIN]: REGION_LANG_TYPES.KSA_AR,
      };
      return REGION_LANG_MAP[Object.keys(REGION_LANG_MAP)?.find((key) => path?.includes(key))];
    },
    [],
  );

  useEffect(() => {
    if (langLoadedRef.current === langCode && selectedLanguageFile !== null) return;
    langLoadedRef.current = langCode;

    const getLangLocales = async () => {
      try {
        const result = await import(`../../Common/i18n/locales/${langCode}.json`);
        setSelectedLanguageFile(result?.default);
      } catch (error) {
        console.error('Error loading language file:', error);
      }
    };
    if(langCode) getLangLocales();
  }, [langCode, selectedLanguageFile]);

  const updateCookieValues = useCallback((newRegionLang) => {
    const selectedRegionLang = COUNTRY_LIST.find((regLang) => regLang?.regionName === newRegionLang);
    if (selectedRegionLang) {
      setDataToCookie('regionCode', selectedRegionLang.regionCode);
      setDataToCookie('langCode', selectedRegionLang.langCode);
      setDataToCookie('timezone', selectedRegionLang.timezone);
    }
  }, []);

  useEffect(() => {
    if (initialized.current) return;
    initialized.current = true;

    const countryAndLang = getRegionLangByUrl(router?.asPath);
    setRegionLang(countryAndLang);
    updateCookieValues(countryAndLang);
  }, [router.asPath, getRegionLangByUrl, updateCookieValues]);

  const replaceFirstSegment = (url, newSegment) => url?.replace(/^\/([^\/]+)\//, `/${newSegment}/`);

  const updateRegionLang = useCallback(
    (newRegionLang, type) => {
      setRegionLang(newRegionLang);
      updateCookieValues(newRegionLang);
      const { asPath } = router;
      const newPath = `${REDIRECT_URI}/${
        type === 'region' ? newRegionLang : replaceFirstSegment(asPath, newRegionLang)
      }`;
      window.location.assign(newPath);
    },
    [updateCookieValues, router],
  );

  const langValues = useMemo(() => ({ regionLang, updateRegionLang }), [regionLang, updateRegionLang]);

  return (
    <RegionLanguageContext.Provider value={langValues}>
      {langCode && selectedLanguageFile ? (
        <IntlProvider locale={langCode} messages={selectedLanguageFile} timeZone="Asia/Dubai">
          {children}
        </IntlProvider>
      ) : null}
    </RegionLanguageContext.Provider>
  );
}

export const useRegionLanguage = () => useContext(RegionLanguageContext);
